import { routes } from './constants';

/**
 * Which Super Admin dashboard section a page belongs to, for custom roles
 * (server: `rbac/sections.ts`, same keys). The sidebar, the profile menu and
 * the page guard all read this, so a page is shown and reachable together.
 * The server enforces the same grants on every call regardless.
 *
 * `always` pages are open to every staff member; `superAdmin` pages to a
 * Super Admin only.
 */
const BUILT_IN_ROLES = ['Super Admin', 'Admin', 'Agent', 'Invited User'];

/** SalesRook staff: a Super Admin or anyone on a custom role (any role not built in). */
export function isStaffRole(name: string | undefined): boolean {
  return Boolean(name) && (name === 'Super Admin' || !BUILT_IN_ROLES.includes(name as string));
}

export type PageAccess = { section: string } | 'always' | 'superAdmin';

// Longest prefix first: '/settings/apis' before '/settings', '/billings' is not '/bills'.
const PAGES: [string, PageAccess][] = [
  [routes.SA_MyProfile, 'always'],
  [routes.SA_Roles, 'superAdmin'],
  [routes.SA_ApiKeys, { section: 'apiKeys' }],
  [routes.SA_Settings, { section: 'integrations' }],
  [routes.SA_OAuthGhl, { section: 'integrations' }],
  [routes.SA_NegotiatorDetails, { section: 'negotiator' }],
  [routes.SA_Core, { section: 'core' }],
  [routes.SA_BillingTokens, { section: 'core' }],
  [routes.SA_Unified_Users, { section: 'unifiedUsers' }],
  [routes.SA_CustomValues, { section: 'customValues' }],
  [routes.SA_BillingAccounts, { section: 'billingAccounts' }],
  [routes.SA_Bills, { section: 'bills' }],
  [routes.SA_Analytics, { section: 'analytics' }],
  [routes.SA_Artifacts, { section: 'artifacts' }],
  [routes.SA_Jobs, { section: 'tasks' }],
  [routes.SA_ZazuAuditTrails, { section: 'zazuAuditTrails' }],
  [routes.SA_ApiDocs, { section: 'apiDocs' }],
  [routes.SA_Diagnostics, { section: 'errorReports' }],
  [routes.SA_SupportTickets, { section: 'supportTickets' }],
  [routes.SA_DiagnosticsLookup, { section: 'codeLookup' }],
  [routes.SA_MarkupMargins, { section: 'markupMargins' }],
  [routes.SA_AiModels, { section: 'aiModels' }],
  [routes.SA_Lookups, { section: 'lookups' }]
].sort((a, b) => (b[0] as string).length - (a[0] as string).length) as [string, PageAccess][];

export function pageAccess(pathname: string): PageAccess | undefined {
  if (pathname === routes.SA_Negotiator || pathname === '') return { section: 'negotiator' };
  const match = PAGES.find(([path]) => pathname === path || pathname.startsWith(`${path}/`));
  return match?.[1];
}

/** Whether someone with these grants may open the page. Unknown pages are left to the server. */
export function canOpenPage(
  pathname: string,
  access: { role: string; sections: { section: string }[] } | undefined
): boolean {
  if (!access) return false;
  if (access.role === 'Super Admin') return true;
  const page = pageAccess(pathname);
  if (page === undefined || page === 'always') return true;
  if (page === 'superAdmin') return false;
  return access.sections.some((grant) => grant.section === page.section);
}

// Sidebar order, for sending staff without the Negotiator section somewhere useful from '/'.
const LANDING = [
  routes.SA_Negotiator,
  routes.SA_Core,
  routes.SA_Unified_Users,
  routes.SA_CustomValues,
  routes.SA_BillingAccounts,
  routes.SA_Bills,
  routes.SA_Analytics,
  routes.SA_Artifacts,
  routes.SA_Jobs,
  routes.SA_ZazuAuditTrails,
  routes.SA_ApiDocs,
  routes.SA_Diagnostics,
  routes.SA_SupportTickets,
  routes.SA_DiagnosticsLookup,
  routes.SA_Settings,
  routes.SA_ApiKeys,
  routes.SA_MarkupMargins,
  routes.SA_AiModels,
  routes.SA_Lookups,
  routes.SA_MyProfile
];

/** The first page these grants can open, in sidebar order (My Profile at worst). */
export function firstOpenPage(
  access: { role: string; sections: { section: string }[] } | undefined
): string {
  return LANDING.find((path) => canOpenPage(path, access)) ?? routes.SA_MyProfile;
}
